import React, { Fragment, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import { JournalFilters } from "./components/JournalFilters";
import { JournalTable } from "./components/JournalTable";
import { JournalDetailDialog } from "./components/JournalDetailDialog";
import { JournalEntryForm } from "./components/JournalEntryForm";
import { ConfirmationDialog } from "./components/ConfirmationDialog";
import { FilterSidebar } from "../../components/FilterSidebar";
import { useBranch } from "../../components/BranchContext";
import {
  useJournals,
  JournalFilters as HookJournalFilters,
} from "./hooks/useJournals";
import {
  IJournalDetailsData,
  IJournalEntryListItem,
} from "../../accounting.interface";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";

const journalStatusTabs = [
  {
    value: "",
    label: "All",
    color: "#6B7280",
    icon: "list_alt",
    description: "All journal entries",
  },
  {
    value: "Posted",
    label: "Posted",
    color: "#10B981",
    icon: "check_circle",
    description: "Posted entries",
  },
  {
    value: "Draft",
    label: "Draft",
    color: "#F59E0B",
    icon: "edit",
    description: "Draft entries",
  },
];

export const JournalsPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const {
    // State
    filters,
    selectedJournal,
    isDetailDialogOpen,
    isFormDialogOpen,
    isLoading,
    tableData,
    confirmationDialog,

    // Handlers
    handleFiltersChange,
    handleResetFilters,
    handleApplyFilters,
    handleJournalClick,
    handleCloseDetailDialog,
    handleOpenFormDialog,
    handleCloseFormDialog,
    handleEditJournal,
    handleReverseJournal,
    handleDeleteJournal,
    handlePostJournal,
    handleSubmitJournal,
    handleCopyJournalId,
    handlePageChange,
    handlePageSizeChange,
    handleCloseConfirmationDialog,
    handleConfirmAction,
    page,
    rowsPerPage,
    filteredJournals,
  } = useJournals();

  // Sync branch context with local filters when branch changes
  React.useEffect(() => {
    if (filters.selectedBranch !== selectedBranch) {
      handleFiltersChange({ selectedBranch });
    }
  }, [selectedBranch, filters.selectedBranch, handleFiltersChange]);

  // Handle status tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const selectedStatus = journalStatusTabs[newValue]?.value || "all";
    const status =
      selectedStatus === ""
        ? "all"
        : (selectedStatus as "draft" | "posted" | "all");
    handleFiltersChange({ status });
  };

  // Handle search query changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    handleFiltersChange({ narrationContains: query });
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === "selectedBranch") return false; // Exclude branch as it's handled globally
      if (key === "status") return false; // Exclude status as it's handled by tabs
      if (key === "narrationContains") return false; // Exclude search as it's handled by search bar
      return value && value.toString().trim() !== "";
    }).length;
  };

  const handleToggleFilterSidebar = () => {
    setIsFilterSidebarOpen(!isFilterSidebarOpen);
  };

  return (
    <Fragment>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
            >
              Journal Entries
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontSize: "1rem" }}
            >
              Financial transaction records and audit trail
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleOpenFormDialog}
            disabled={
              !hasPermission(
                "accounting.journals.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.5,
            }}
          >
            New Entry
          </Button>
        </Box>

        {/* Status Tabs & Search */}
        <Box
          sx={{
            mb: 3,
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 64,
                px: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {journalStatusTabs.map((statusTab, index) => (
              <Tab
                key={statusTab.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: statusTab.color,
                      }}
                    />
                    <Icon sx={{ fontSize: 18 }}>{statusTab.icon}</Icon>
                    {statusTab.label}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {/* Search & Filters */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3 }}>
            <TextField
              size="small"
              placeholder="Search journals..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                minWidth: 280,
                "& .MuiOutlinedInput-root": {
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.background.default, 0.7),
                  },
                  "&.Mui-focused": {
                    bgcolor: alpha(theme.palette.background.default, 0.9),
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
                      search
                    </Icon>
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Advanced Filters">
              <IconButton
                onClick={handleToggleFilterSidebar}
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor:
                    getActiveFiltersCount() > 0
                      ? "primary.main"
                      : "transparent",
                  color: getActiveFiltersCount() > 0 ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor:
                      getActiveFiltersCount() > 0
                        ? "primary.dark"
                        : "action.hover",
                  },
                }}
              >
                <Icon>tune</Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: "max-content",
            bgcolor: "background.paper",
            borderRadius: 0.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            p: 0,
            position: "relative",
          }}
        >
          <JournalTable
            data={tableData}
            pagination={{
              total: filteredJournals.length,
              page: page,
              pageSize: rowsPerPage,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            onJournalClick={handleJournalClick}
            onReverseJournal={handleReverseJournal}
            onDeleteJournal={handleDeleteJournal}
            onEditJournal={handleEditJournal}
            onPostJournal={handlePostJournal}
            onCopyJournalId={handleCopyJournalId}
          />
        </Box>
      </Box>

      {/* Filter Sidebar */}
      <FilterSidebar
        open={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        title="Journal Filters"
        activeFiltersCount={getActiveFiltersCount()}
        onResetFilters={handleResetFilters}
        onApplyFilters={() => {
          handleApplyFilters();
          setIsFilterSidebarOpen(false);
        }}
      >
        <JournalFilters
          filters={filters as any}
          onFiltersChange={handleFiltersChange as any}
          onResetFilters={handleResetFilters}
          onApplyFilters={handleApplyFilters}
        />
      </FilterSidebar>

      {/* Journal Detail Dialog */}
      <JournalDetailDialog
        open={isDetailDialogOpen}
        onClose={handleCloseDetailDialog}
        journal={selectedJournal as any}
        onReverse={(journal: IJournalDetailsData) =>
          handleReverseJournal(journal as any)
        }
        onEdit={(journal: IJournalDetailsData) =>
          handleEditJournal(journal as any)
        }
        onDelete={(journal: IJournalDetailsData) =>
          handleDeleteJournal(journal as any)
        }
      />

      {/* Journal Entry Form */}
      <JournalEntryForm
        open={isFormDialogOpen}
        onClose={handleCloseFormDialog}
        onSave={handleSubmitJournal}
        isLoading={isLoading}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmAction}
        title={
          confirmationDialog.type === "reverse"
            ? "Reverse Journal Entry"
            : confirmationDialog.type === "delete"
            ? "Delete Journal Entry"
            : confirmationDialog.type === "post"
            ? "Post Journal Entry"
            : ""
        }
        message={
          confirmationDialog.type === "reverse"
            ? `Are you sure you want to reverse journal entry ${confirmationDialog.journal?.journal_entry_number}? This will create a new reversing entry with swapped debits and credits.`
            : confirmationDialog.type === "delete"
            ? `Are you sure you want to delete journal entry ${confirmationDialog.journal?.journal_entry_number}? This action cannot be undone.`
            : confirmationDialog.type === "post"
            ? `Are you sure you want to post journal entry ${confirmationDialog.journal?.journal_entry_number} to the ledger? Once posted, this entry cannot be edited or deleted.`
            : ""
        }
        confirmText={
          confirmationDialog.type === "reverse"
            ? "Reverse Entry"
            : confirmationDialog.type === "delete"
            ? "Delete Entry"
            : confirmationDialog.type === "post"
            ? "Post Entry"
            : "Confirm"
        }
        severity={
          confirmationDialog.type === "delete"
            ? "error"
            : confirmationDialog.type === "post"
            ? "info"
            : "warning"
        }
        isLoading={isLoading}
        details={
          confirmationDialog.type === "reverse"
            ? [
                "A new journal entry will be created with reversed debits and credits",
                "Both entries will remain in the system for audit purposes",
                "The reversing entry will be automatically posted",
              ]
            : confirmationDialog.type === "delete"
            ? [
                "This will permanently remove the journal entry",
                "Only draft entries can be deleted",
                "This action cannot be undone",
              ]
            : confirmationDialog.type === "post"
            ? [
                "The entry will be permanently recorded in the ledger",
                "Posted entries cannot be edited or deleted",
                "Only reversing entries can be created for corrections",
                "All account balances will be updated accordingly",
              ]
            : []
        }
      />
    </Fragment>
  );
};
